/*
 * Essential Upsell cross-sell widget: shows a loading state on click, then an "Added to cart"
 * state after a successful add, and disables variants whose whole stock is already in the cart
 * (the app keeps offering them and the add then fails silently with a 422). When the selected
 * variant runs out, the next variant in stock is selected; if none is left, the default variant
 * is selected and the button shows "Out of stock".
 *
 * The app renders and re-renders the widget itself, so state lives here and is re-applied after
 * every re-render. Only attributes are touched on app-owned nodes; their children are left alone.
 */
(() => {
  const ROOT = 'essential-upsell-cross-sell-app-block';
  const PRODUCT = '[data-essential-upsell-element="product"]';
  const BUTTON = '[data-essential-upsell-element="add-to-cart-button"]';
  
  const ADDED_FLASH_MS = 2000;
  const LOADING_TIMEOUT_MS = 10000;
  const CART_MUTATION_URL = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  const translation = (window.PXUTheme && window.PXUTheme.translation) || {};
  const labels = {
    loading: translation.upsell_adding || 'Adding...',
    added: translation.upsell_added_to_cart || 'Added to cart',
    outOfStock: translation.upsell_out_of_stock || 'Out of stock',
  };
  const routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const AUTO_SELECT_RETRY_MS = 1000;
  const setSelectValue = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value').set;

  const productRequests = new Map();
  const productsByHandle = new Map();
  const defaultVariants = new Map();
  const autoSelections = new WeakMap();
  const inventory = new Map();
  const recentlyAdded = new Map();
  const pendingAdds = new Map();
  const loadingTimeouts = new Map();
  let cartQuantities = new Map();
  let applyScheduled = false;
  let cartRefreshTimer = null;
  let liveRegion = null;

  function handleFor(productEl) {
    const link = productEl.querySelector('a[href*="/products/"]');
    if (!link) return null;
    const match = new URL(link.href, window.location.origin).pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function loadProduct(handle) {
    if (!productRequests.has(handle)) {
      const request = fetch(`${routesRoot}products/${handle}.js`)
        .then(response => (response.ok ? response.json() : null))
        .then(product => {
          if (!product) return;
          productsByHandle.set(handle, product);
          product.variants.forEach(variant => {
            inventory.set(String(variant.id), {
              tracked: variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny',
              quantity: variant.inventory_quantity,
            });
          });
          scheduleApply();
        })
        .catch(() => {});
      productRequests.set(handle, request);
    }
    return productRequests.get(handle);
  }

  function variantIdFor(productEl) {
    const select = productEl.querySelector('select');
    if (select) return select.value;
    const product = productsByHandle.get(handleFor(productEl));
    return product && product.variants.length === 1 ? String(product.variants[0].id) : null;
  }

  function remaining(variantId) {
    const stock = inventory.get(String(variantId));
    if (!stock || !stock.tracked) return Infinity;
    return stock.quantity - (cartQuantities.get(String(variantId)) || 0);
  }

  function announce(message) {
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('role', 'status');
      liveRegion.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;';
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    window.setTimeout(() => {
      liveRegion.textContent = message;
    }, 50);
  }

  function labelFor(state) {
    if (state === 'loading') return labels.loading;
    if (state === 'added') return labels.added;
    return labels.outOfStock;
  }

  function setButtonState(button, state, productTitle) {
    const current = button.dataset.themeUpsellState || null;
    if (state === current) return;

    if (!state) {
      delete button.dataset.themeUpsellState;
      delete button.dataset.themeUpsellLabel;
      button.disabled = false;
      if ('themeUpsellAriaLabel' in button.dataset) {
        button.setAttribute('aria-label', button.dataset.themeUpsellAriaLabel);
        delete button.dataset.themeUpsellAriaLabel;
      }
      return;
    }

    const label = labelFor(state);
    if (!('themeUpsellAriaLabel' in button.dataset)) {
      button.dataset.themeUpsellAriaLabel = button.getAttribute('aria-label') || '';
    }
    button.dataset.themeUpsellState = state;
    button.dataset.themeUpsellLabel = label;
    button.setAttribute('aria-label', productTitle ? `${label}: ${productTitle}` : label);

    // Defer disabling on loading so this click still reaches the app's add-to-cart handler
    // (we listen in capture). Added / out-of-stock can disable immediately.
    if (state === 'loading') {
      window.setTimeout(() => {
        if (button.dataset.themeUpsellState === 'loading') button.disabled = true;
      }, 0);
    } else {
      button.disabled = true;
    }
  }

  function clearLoadingTimeout(variantId) {
    window.clearTimeout(loadingTimeouts.get(variantId));
    loadingTimeouts.delete(variantId);
  }

  function clearPendingAdd(variantId) {
    pendingAdds.delete(variantId);
    clearLoadingTimeout(variantId);
  }

  function nextAvailableOption(select) {
    const options = Array.from(select.options);
    for (let step = 1; step < options.length; step += 1) {
      const option = options[(select.selectedIndex + step) % options.length];
      if (!option.disabled && remaining(option.value) > 0) return option;
    }
    return null;
  }

  // The select is controlled by the app, so set the value natively and let its change handler pick it up.
  function selectVariant(select, value) {
    const last = autoSelections.get(select);
    if (last && last.value === value && Date.now() - last.at < AUTO_SELECT_RETRY_MS) return;
    autoSelections.set(select, { value, at: Date.now() });
    setSelectValue.call(select, value);
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function applyProduct(productEl) {
    const button = productEl.querySelector(BUTTON);
    if (!button) return;

    const handle = handleFor(productEl);
    if (handle) loadProduct(handle);

    const select = productEl.querySelector('select');
    if (select && handle && !defaultVariants.has(handle)) defaultVariants.set(handle, select.value);
    if (select) {
      Array.from(select.options).forEach(option => {
        const exhausted = remaining(option.value) <= 0;
        if (exhausted && !option.disabled) {
          option.disabled = true;
          option.dataset.themeUpsellDisabled = '';
        } else if (!exhausted && 'themeUpsellDisabled' in option.dataset) {
          option.disabled = false;
          delete option.dataset.themeUpsellDisabled;
        }
      });
    }

    const variantId = variantIdFor(productEl);
    let state = null;
    if (variantId && recentlyAdded.has(variantId)) {
      state = 'added';
    } else if (variantId && pendingAdds.has(variantId)) {
      state = 'loading';
    } else if (variantId && remaining(variantId) <= 0) {
      const next = select && nextAvailableOption(select);
      if (next) {
        selectVariant(select, next.value);
        return;
      }
      const fallback = defaultVariants.get(handle);
      if (select && fallback && select.value !== fallback) {
        selectVariant(select, fallback);
        return;
      }
      state = 'out-of-stock';
    }
    const product = productsByHandle.get(handle);
    setButtonState(button, state, product && product.title);
  }

  function applyAll() {
    applyScheduled = false;
    document.querySelectorAll(`${ROOT} ${PRODUCT}`).forEach(applyProduct);
  }

  function scheduleApply() {
    if (applyScheduled) return;
    applyScheduled = true;
    window.setTimeout(applyAll, 0);
  }

  function flashAdded(variantId) {
    clearPendingAdd(variantId);
    window.clearTimeout(recentlyAdded.get(variantId));
    recentlyAdded.set(
      variantId,
      window.setTimeout(() => {
        recentlyAdded.delete(variantId);
        scheduleApply();
      }, ADDED_FLASH_MS),
    );
    announce(labels.added);
  }

  async function refreshCart() {
    let cart;
    try {
      cart = await (await fetch(`${routesRoot}cart.js`)).json();
    } catch (error) {
      return;
    }

    const quantities = new Map();
    cart.items.forEach(item => {
      const id = String(item.variant_id);
      quantities.set(id, (quantities.get(id) || 0) + item.quantity);
    });
    cartQuantities = quantities;

    Array.from(pendingAdds.keys()).forEach(variantId => {
      const quantityBefore = pendingAdds.get(variantId);
      if ((cartQuantities.get(variantId) || 0) > quantityBefore) {
        flashAdded(variantId);
      } else {
        clearPendingAdd(variantId);
      }
    });
    scheduleApply();
  }

  function scheduleCartRefresh() {
    window.clearTimeout(cartRefreshTimer);
    cartRefreshTimer = window.setTimeout(refreshCart, 150);
  }

  function watchCartRequests() {
    if (!('PerformanceObserver' in window)) return;
    new PerformanceObserver(list => {
      const touchedCart = list.getEntries().some(
        entry =>
          (entry.initiatorType === 'fetch' || entry.initiatorType === 'xmlhttprequest') &&
          CART_MUTATION_URL.test(new URL(entry.name, window.location.origin).pathname),
      );
      if (touchedCart) scheduleCartRefresh();
    }).observe({ type: 'resource', buffered: false });
  }

  function init() {
    const roots = document.querySelectorAll(ROOT);
    if (!roots.length) return;

    roots.forEach(root => {
      new MutationObserver(scheduleApply).observe(root, { childList: true, subtree: true });
    });

    // The app stops variant-select change events from bubbling, so listen in the capture phase.
    document.addEventListener(
      'change',
      event => {
        if (event.target.closest(ROOT)) scheduleApply();
      },
      true,
    );

    document.addEventListener(
      'click',
      event => {
        const button = event.target.closest(BUTTON);
        const productEl = button && button.closest(`${ROOT} ${PRODUCT}`);
        if (!productEl || !button || button.disabled) return;

        const variantId = variantIdFor(productEl);
        if (!variantId || remaining(variantId) <= 0 || pendingAdds.has(variantId)) return;

        pendingAdds.set(variantId, cartQuantities.get(variantId) || 0);
        clearLoadingTimeout(variantId);
        loadingTimeouts.set(
          variantId,
          window.setTimeout(() => {
            clearPendingAdd(variantId);
            scheduleApply();
          }, LOADING_TIMEOUT_MS),
        );

        const product = productsByHandle.get(handleFor(productEl));
        setButtonState(button, 'loading', product && product.title);
      },
      true,
    );

    watchCartRequests();
    refreshCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
